// client/src/api/configure.ts — the generated client, configured ONCE · PROP-004 (04 §9.3.2).
//
// This is the file §9.3.2 names and §2.1 places: `configureApiClient` over `src/client/**`,
// the @hey-api/openapi-ts output the drift gate keeps equal to `openapi/v1.json` (DEVOPS-003).
// It does for the generated client exactly what `api/http.ts` does for the hand-written
// wrapper, and it does it with the SAME hooks — `connectHttp` (session/source.ts) hands one
// `HttpHooks` object to both, so a token refreshed on one path is the token the other sends.
//
//   • request  — refresh ahead of expiry, then the bearer, then an `X-Correlation-Id` the API
//                echoes and logs (D7) and the `X-Requested-With` marker of 02 §18;
//   • response — 401 → single-flight refresh (§9.3.3) → ONE replay with the fresh token; a
//                refresh that fails ends the session. The `/auth/*` routes are never replayed
//                (`isReplayable`), because a 401 there is an answer, not an expired token;
//   • error    — the client throws the parsed problem body as a bare object; this turns it into
//                the `ApiError` every error surface already understands (`problemCode`,
//                `ErrorState`, the per-feature message tables), so a feature cannot tell which
//                transport a failure came through — and must not care.
//   • in flight — a keyed call the server answers 409 `idempotency-request-in-progress` is sent
//                again, under the SAME key and body, after a short pause, a bounded number of
//                times (D-2618). The answer means a twin of this very call is still running
//                (02 §7.5, D-2616); once it commits, the resend replays its response and the
//                screen sees the success it was waiting for. Not a retry of a failure: the key
//                is exactly what makes a resend unable to do the work twice.
//
// `api/http.ts` stays for `authApi` until AUTH-004's identity operations move onto the SDK;
// nothing else may use it for API data (04 §9.4 rule 1, and the guard in
// `api/__tests__/resource-clients.test.ts`).

import { client } from '@/client/client.gen';
import { apiBaseUrl } from '@/config/app-meta';

import { ApiError, isReplayable, type HttpHooks } from './http';
import { toProblemDetails } from './problem-details';

let hooks: HttpHooks | undefined;
let installed = false;

/**
 * Called once from the session source with the hooks; `undefined` disconnects (tests). The
 * interceptors are installed on the first call and thereafter read `hooks` at request time,
 * so reconnecting never stacks a second set.
 */
export function configureApiClient(next: HttpHooks | undefined): void {
  hooks = next;
  if (installed) return;
  installed = true;

  client.setConfig({
    // `''` for same-origin (02 §16); the client resolves `/api/v1/…` against the document.
    baseUrl: apiBaseUrl(),
    // The refresh cookie rides along on its own path; nothing here needs to know it exists.
    credentials: 'same-origin',
    headers: {
      Accept: 'application/json',
      // 02 §18 names this as the SPA's marker on state-changing auth calls; sent everywhere so
      // the server may start checking it without a client release.
      'X-Requested-With': 'Proprietari360',
    },
  });

  client.interceptors.request.use(async (request) => {
    const replayable = isReplayable(apiPath(request.url));
    if (hooks && replayable) await hooks.ensureFreshToken();

    const token = hooks?.getAccessToken();
    if (token) request.headers.set('Authorization', `Bearer ${token}`);
    // Echoed and logged by the API (D7, 02 §19): the one string a user can read to support.
    request.headers.set('X-Correlation-Id', crypto.randomUUID());
    return request;
  });

  client.interceptors.response.use(async (response, request, options) => {
    if (response.status !== 401 || hooks === undefined || !isReplayable(apiPath(request.url))) {
      return response;
    }

    if (await hooks.refreshSession()) {
      // Replay ONCE with the fresh token. The original Request's body stream is spent, so the
      // replay is rebuilt from what the client serialised; a second 401 is a real one and is
      // returned as such.
      return (options.fetch ?? globalThis.fetch)(resendOf(request, options.serializedBody));
    }

    hooks.onUnauthenticated();
    return response;
  });

  client.interceptors.response.use(async (response, request, options) => {
    // D-2618: only a keyed call, and only this one answer. Every other 409 — a concurrency
    // conflict, a taken slug — is a real refusal and reaches the screen untouched.
    if (!request.headers.has(IDEMPOTENCY_HEADER) || !(await isRequestInProgress(response))) {
      return response;
    }

    let answer = response;
    for (const pause of IN_PROGRESS_RESEND_PAUSES_MS) {
      await new Promise((resolve) => setTimeout(resolve, pause));
      answer = await (options.fetch ?? globalThis.fetch)(resendOf(request, options.serializedBody));
      if (!(await isRequestInProgress(answer))) return answer;
    }
    // Still running after every pause: the 409 goes to the screen, whose message table renders
    // the server's own sentence — „Aceeași cerere este deja în curs de procesare…" — which
    // tells the user to refresh in a moment rather than that anything failed.
    return answer;
  });

  client.interceptors.error.use((error, response) => {
    // No response at all — a rejected fetch — is reported as the transport failure it is; the
    // feature tables read that as „network" (they check `instanceof ApiError`).
    if (response === undefined || error instanceof ApiError) return error;

    const problem = typeof error === 'string' && error.trim() === '' ? {} : toProblemDetails(error);
    const headerCorrelation = response.headers.get('X-Correlation-Id');

    return new ApiError(response.status, {
      ...problem,
      status: response.status,
      // The API writes the id into the body; a 429 from the limiter or a 502 from the proxy
      // may only carry the header. Either way it is the support handle (§5.11).
      ...(problem.correlationId === undefined && headerCorrelation ? { correlationId: headerCorrelation } : {}),
    });
  });
}

/** 02 §7.5's header. A request that carries it may be sent again without doing the work twice. */
const IDEMPOTENCY_HEADER = 'Idempotency-Key';

/** The problem `type` of D-2616's answer. */
const IN_PROGRESS_TYPE = 'https://proprietari360.ro/errors/idempotency-request-in-progress';

/**
 * The pauses before each resend of a call whose twin is still running — three resends over
 * about three and a half seconds. Enough for any keyed mutation in the product to commit; short
 * enough that a user who is still waiting after it is told why rather than left looking at a
 * spinner.
 */
export const IN_PROGRESS_RESEND_PAUSES_MS: readonly number[] = [500, 1000, 2000];

/** Whether the server answered D-2616's 409 — read from a clone, so the body stays unread. */
async function isRequestInProgress(response: Response): Promise<boolean> {
  if (response.status !== 409) return false;
  try {
    const body: unknown = await response.clone().json();
    return (
      typeof body === 'object' &&
      body !== null &&
      (body as { type?: unknown }).type === IN_PROGRESS_TYPE
    );
  } catch {
    return false;
  }
}

/**
 * The same call again: same method, same headers — the `Idempotency-Key` among them — and the
 * body the client serialised, because the original Request's stream is spent. The bearer is
 * the current one and the correlation id is new, since this is a new request on the wire.
 */
function resendOf(request: Request, serializedBody: unknown): Request {
  const resend = new Request(request.url, {
    method: request.method,
    headers: new Headers(request.headers),
    body: (serializedBody ?? null) as BodyInit | null,
    credentials: request.credentials,
    signal: request.signal,
  });
  const token = hooks?.getAccessToken();
  if (token) resend.headers.set('Authorization', `Bearer ${token}`);
  else resend.headers.delete('Authorization');
  resend.headers.set('X-Correlation-Id', crypto.randomUUID());
  return resend;
}

/**
 * The API-relative path of a request URL — `/api/v1/auth/login` — whatever base the client
 * was given. `isReplayable` is written against paths, and a base URL that carries a prefix
 * would otherwise hide the `/auth/` segment from it.
 */
function apiPath(url: string): string {
  const placeholder = 'http://placeholder.invalid';
  let pathname: string;
  try {
    pathname = new URL(url, placeholder).pathname;
  } catch {
    return url;
  }
  const base = new URL(apiBaseUrl() || '/', placeholder).pathname.replace(/\/$/, '');
  return base !== '' && pathname.startsWith(base) ? pathname.slice(base.length) : pathname;
}
